import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Gem, Wallet, Loader2 } from "lucide-react";

/** Payout request widget — embedded in the Dashboard page. */
export const PayoutWidget = () => {
  const { user, refresh } = useAuth();
  const [tipsInfo, setTipsInfo] = useState({ total_earned_gems: 0, received: [], sent: [] });
  const [payouts, setPayouts] = useState({ payouts: [], total_paid_gems: 0, min_payout_gems: 1000 });
  const [open, setOpen] = useState(false);
  const [gems, setGems] = useState(1000);
  const [method, setMethod] = useState("paypal");
  const [destination, setDestination] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/me/tips").then(r => setTipsInfo(r.data)).catch(() => {});
    api.get("/me/payouts").then(r => setPayouts(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return null;

  const min = payouts.min_payout_gems || 1000;
  const canRequest = (user.credits || 0) >= min;

  const submit = async (e) => {
    e.preventDefault();
    if (!destination.trim()) { toast.error("Enter a destination (PayPal email, Zelle handle, etc)"); return; }
    setBusy(true);
    try {
      await api.post("/me/payouts/request", { gems: Number(gems), method, destination: destination.trim(), note });
      toast.success(`Payout request submitted — $${(gems/100).toFixed(2)}`);
      setOpen(false);
      setDestination(""); setNote(""); setGems(1000);
      await refresh();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="payout-widget" className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-rose-500/5 p-5 mb-6">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-amber-300 mb-1">💎 Earnings &amp; Payouts</div>
          <div className="font-serif text-2xl text-white font-bold">${((user.credits || 0)/100).toFixed(2)} <span className="text-sm text-slate-400 font-normal">available ({user.credits || 0} gems)</span></div>
          <div className="text-xs text-slate-400 mt-1">
            Earned from tips: <span className="text-amber-300">{tipsInfo.total_earned_gems} gems</span>
            {" · "}
            Paid out: <span className="text-emerald-300">{payouts.total_paid_gems} gems</span>
          </div>
        </div>
        <Button
          data-testid="payout-request-btn"
          onClick={() => setOpen(o => !o)}
          disabled={!canRequest}
          className="rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold"
          title={canRequest ? "" : `Need at least ${min} gems to cash out`}
        >
          <Wallet className="h-4 w-4 mr-1" /> Request payout
        </Button>
      </div>
      {!canRequest && (
        <div className="text-[11px] text-slate-500 font-mono">Minimum payout is {min} gems (~${(min/100).toFixed(0)}). Earn more via tips first.</div>
      )}

      {open && (
        <form onSubmit={submit} data-testid="payout-form" className="mt-4 rounded-xl border border-white/10 bg-[#0B0D13] p-4 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">Gems to cash out</Label>
              <Input data-testid="payout-gems" type="number" min={min} max={user.credits || 0} value={gems}
                     onChange={e => setGems(e.target.value)} className="mt-1 bg-[#07080A] border-white/10 h-11" />
              <div className="text-[11px] text-amber-300 mt-1">≈ ${(Number(gems)/100 || 0).toFixed(2)}</div>
            </div>
            <div>
              <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="payout-method" className="mt-1 bg-[#07080A] border-white/10 h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                  <SelectItem value="stripe">Stripe (coming soon)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">Destination</Label>
            <Input data-testid="payout-destination" value={destination} onChange={e => setDestination(e.target.value)}
                   placeholder={method === "paypal" ? "paypal@example.com" : method === "zelle" ? "+1 555 123 4567 or email" : "Account details"}
                   className="mt-1 bg-[#07080A] border-white/10 h-11" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">Note (optional)</Label>
            <Input data-testid="payout-note" value={note} onChange={e => setNote(e.target.value)}
                   placeholder="Anything we should know?" className="mt-1 bg-[#07080A] border-white/10 h-11" />
          </div>
          <Button data-testid="payout-submit" type="submit" disabled={busy}
                  className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit request · {gems} gems</>}
          </Button>
          <div className="text-[11px] text-slate-500 font-mono">
            Gems are held once you submit. Admin usually reviews within 3 business days. If rejected, gems are refunded.
          </div>
        </form>
      )}

      {payouts.payouts.length > 0 && (
        <div data-testid="payout-history" className="mt-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Recent payouts</div>
          <div className="space-y-1 text-sm">
            {payouts.payouts.slice(0, 5).map(p => (
              <div key={p.payout_id} className="flex items-center justify-between rounded-lg bg-black/30 border border-white/5 px-3 py-2">
                <div>
                  <div className="text-slate-200">{p.gems} gems · ${p.usd_amount} · {p.method}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{new Date(p.created_at).toLocaleDateString()}</div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${
                  p.status === "paid" ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40" :
                  p.status === "rejected" ? "bg-rose-500/20 text-rose-200 border-rose-400/40" :
                  "bg-amber-500/20 text-amber-200 border-amber-400/40"
                }`}>{p.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutWidget;
